import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth/useAuth';
import { useBackEndRequests } from '../../backEndRequests';
import { environment, urlLocal } from '../../../environment/environment';
import { useInView } from '../../../hooks/useInView';
import styles from './Servicos.module.scss';

const SITE_ENVIRONMENT = environment.environment;
const LOCAL_DEV_URL = urlLocal;

interface ServiceCardData {
  readonly icon: string;
  readonly title: string;
  readonly description: string;
  readonly onClick?: () => void;
  readonly infoPath?: string;
}

function ActiveCard({
  icon,
  title,
  description,
  delay,
  onClick,
  infoPath,
}: Readonly<ServiceCardData & { delay: number }>) {
  const [ref, inView] = useInView<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`${styles.activeCard} ${inView ? 'inView' : ''}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      <div className={styles.iconGreen}>
        <i className={`fas fa-${icon}`} aria-hidden='true' />
      </div>
      <p className={styles.cardTitle}>{title}</p>
      <p className={styles.cardDesc}>{description}</p>
      <div className={styles.cardActions}>
        {onClick && (
          <button className={styles.accessBtn} onClick={onClick} type='button'>
            Acessar <i className={`fas fa-arrow-right`} aria-hidden='true' />
          </button>
        )}
        {infoPath && (
          <Link to={infoPath} className={styles.infoLink}>
            Saiba mais
          </Link>
        )}
      </div>
    </div>
  );
}

function ComingSoonCard({ delay }: Readonly<{ delay: number }>) {
  const [ref, inView] = useInView<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`${styles.comingSoon} ${inView ? 'inView' : ''}`}
      style={{ transitionDelay: `${delay}s` }}
      aria-label='Em breve: Caixa Postal Regulatória, Painel de Inteligência Mineral, Autorizações e gestão de termos'
    >
      <div className={styles.iconGray}>
        <i className={`fas fa-book`} aria-hidden='true' />
      </div>
      <p className={styles.cardTitle}>Em breve...</p>
      <ul className={styles.list}>
        <li>Caixa Postal Regulatória</li>
        <li>Painel de Inteligência Mineral</li>
        <li>Autorizações e gestão de termos</li>
      </ul>
    </div>
  );
}

interface ServicosProps {
  readonly variant: 'deslogado' | 'logado';
}

export function Servicos({ variant }: ServicosProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const isLogado = variant === 'logado';
  const title = isLogado ? 'Serviços Disponíveis' : 'Serviços';

  const { getIsDistribuidorAutorizado } = useBackEndRequests();
  const isAutenticado = auth?.isAuthenticated;
  const isAmbienteDesenvolvimento =
    SITE_ENVIRONMENT === 'des' || SITE_ENVIRONMENT === 'local';

  const [mostrarCardDistribuicao, setMostrarCardDistribuicao] =
    React.useState(false);

  const redireciona = (rota: string) => {
    globalThis.location.href = `${globalThis.location.origin}/${rota}`;
  };

  const redirecionaLocal = (ambiente: string) => {
    globalThis.location.href = `${LOCAL_DEV_URL}${ambiente}?token=${auth?.token}`;
  };

  React.useEffect(() => {
    let isAtivo = true;
    const carregar = async () => {
      if (!isAutenticado) {
        if (isAtivo) setMostrarCardDistribuicao(false);
        return;
      }
      try {
        const autorizado = await getIsDistribuidorAutorizado();
        if (isAtivo) setMostrarCardDistribuicao(autorizado);
      } catch {
        if (isAtivo) setMostrarCardDistribuicao(false);
      }
    };
    carregar();
    return () => {
      isAtivo = false;
    };
  }, [isAutenticado, getIsDistribuidorAutorizado]);

  return (
    <div
      className={`${styles.section} ${isLogado ? styles.logado : styles.deslogado}`}
      data-name='servicos'
    >
      <p
        className={`${styles.title} ${isLogado ? styles.titleDark : styles.titleBrand}`}
      >
        {title}
      </p>
      <div className={styles.cards}>
        <ActiveCard
          icon='file-alt'
          title='DIEF-CFEM'
          description='DIEF-CFEM é a declaração mensal que detalha como o valor da CFEM será apurado.'
          delay={0}
          onClick={() => redireciona('dief')}
          infoPath='/info-dief'
        />
        <ActiveCard
          icon='money-bill-wave'
          title='Arrecadação-CFEM'
          description='CFEM é a Compensação Financeira pela Exploração Mineral e o pagamento deve ser mensal.'
          delay={0.12}
          onClick={() => redireciona('arrecadacao')}
          infoPath='/info-arrecadacao'
        />
        {isLogado && mostrarCardDistribuicao ? (
          <ActiveCard
            icon='chart-pie'
            title='Distribuição'
            description='Gerencie a distribuição da CFEM entre os beneficiários, acompanhe repasses e emita relatórios de distribuição.'
            delay={0.24}
            onClick={() => redireciona('distribuicao')}
          />
        ) : (
          !isLogado && <ComingSoonCard delay={0.24} />
        )}
      </div>

      {isAmbienteDesenvolvimento && (
        <div className={styles.devSection}>
          <p className={styles.devTitle}>
            <i className={`fas fa-code`} aria-hidden='true' /> Login em
            Desenvolvimento
          </p>
          <div className={styles.cards}>
            <ActiveCard
              icon='code'
              title='Dev — DIEF-CFEM'
              description='Acesso local ao módulo DIEF-CFEM'
              delay={0}
              onClick={() =>
                isAutenticado ? redirecionaLocal('dief') : navigate('/login')
              }
            />
            <ActiveCard
              icon='code'
              title='Dev — Arrecadação'
              description='Acesso local ao módulo Arrecadação'
              delay={0.08}
              onClick={() =>
                isAutenticado
                  ? redirecionaLocal('arrecadacao')
                  : navigate('/login')
              }
            />
            <ActiveCard
              icon='code'
              title='Dev — Distribuição'
              description='Acesso local ao módulo Distribuição'
              delay={0.16}
              onClick={() =>
                isAutenticado
                  ? redirecionaLocal('distribuicao')
                  : navigate('/login')
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
